import {
  array,
  bool,
  func,
  number,
  oneOf,
  oneOfType,
  string,
} from "prop-types";
import { forwardRef, useMemo } from "react";

import styled from "styled-components";
import {
  backgroundColor,
  negitiveMargin,
  shadeOnHover,
  themeVariants,
} from "theme";

import FnrTypography from "FnrTypography";

import FnrCard from "UI/FnrCard/FnrCard";
import FnrChevron from "UI/FnrChevron/FnrChevron";
import FnrMovingDiv from "UI/FnrMovingDiv";
import FnrSlides from "UI/FnrSlides";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap/FnrHorizontalGap";

const PLACEHOLDER_KEY = "placeholder";

const Selector = styled(FnrCard)`
  overflow: hidden;
  position: relative;
  border: 1px solid
    ${({ $borderColor, theme: { colors } }) =>
      colors[$borderColor] || $borderColor};
  padding: 8px
    ${({ $variant }) => ($variant === themeVariants.emphasized ? "12px" : "")};
  border-radius: ${({ $variant }) =>
    $variant === themeVariants.emphasized ? 8 : 10}px;
`;

const innerDivStylesByVariant = ({ $variant }) => {
  switch ($variant) {
    case "emphasized":
      return `
        padding: 16px 12px;
      `;
    default:
      return "";
  }
};

const SFnrHorizontalGap = styled(FnrHorizontalGap)`
  ${negitiveMargin}
  ${innerDivStylesByVariant}
  ${backgroundColor}
  ${shadeOnHover}
  border-radius: 6px;
`;

const variantProps = {
  color: { emphasized: "black", default: "textPrimary" },
  fontWeight: { emphasized: 300, default: 400 },
};

const FnrSelectionDisplay = forwardRef(
  /**
   *
   * @param {object} param0
   * @param {function} param0.onClick
   * @param {string} param0.placeholder - what to display when nothing is selected
   * @param {[SelectListOption]} param0.options - the options to be displayed
   * @param {boolean} param0.disabled
   * @param {color} param0.borderColor
   * @param {"default"|"emphasized"} param0.variant - set to emphasized to add some OOMF. TODO: make this a boolean: emphasized.
   * @param {boolean} param0.multiselect - Allows multiple options to be selected. TODO: refactor to make this smart enough to not need this prop.
   * @param {number|string|array} param0.selectedId - the selected ID(s)
   * @param {boolean} param0.active - passed down to the chevron
   * @param {import("react").Ref} ref
   * @returns
   */
  (
    {
      onClick,
      placeholder,
      options = [],
      disabled,
      borderColor = "divider",
      variant,
      multiselect,
      selectedId,
      active,
      ...rest
    },
    ref
  ) => {
    const slideComponents = useMemo(
      () =>
        [{ id: PLACEHOLDER_KEY, label: placeholder }, ...options].map(
          (option) => ({
            key: option?.id?.toString(),
            value: () => (
              <FnrTypography
                color={disabled ? "divider" : variantProps.color[variant]}
                fontWeight={variantProps.fontWeight[variant]}
                noWrap
              >
                {option?.label || option?.label}
              </FnrTypography>
            ),
          })
        ),
      [disabled, options, placeholder, variant]
    );

    return (
      <Selector
        noGradient
        ref={ref}
        onClick={onClick}
        $borderColor={borderColor}
        disabled={disabled}
        $variant={variant}
        {...rest}
      >
        <SFnrHorizontalGap
          fullWidth
          $variant={variant}
          $noShade={variant === "default"}
        >
          <FnrMovingDiv widthMotion clip={Boolean(multiselect)}>
            {multiselect ? (
              <FnrTypography
                color={disabled ? "divider" : variantProps.color[variant]}
                fontWeight={variantProps.fontWeight[variant]}
                noWrap
              >
                {selectedId
                  .map((currentId) => {
                    return options.find(({ id }) => id === currentId).label;
                  })
                  .join(", ")}
              </FnrTypography>
            ) : (
              <FnrSlides
                fullWidth
                activeKey={selectedId?.toString() || PLACEHOLDER_KEY}
                components={slideComponents}
                variant="subtle"
              />
            )}
          </FnrMovingDiv>
          <FnrChevron active={active} disabled={disabled} />
        </SFnrHorizontalGap>
      </Selector>
    );
  }
);

FnrSelectionDisplay.propTypes = {
  onClick: func,
  placeholder: string,
  options: array,
  disabled: bool,
  borderColor: string,
  variant: oneOf(["default", "emphasized"]),
  multiselect: bool,
  selectedId: oneOfType([number, string, array]),
  active: bool,
};

export default FnrSelectionDisplay;
